import { sectionType } from "../../../sectionType";
import { ServiceManager } from "../../../services/service-manager";
import { ColumnBaseService } from "../column-base.service";
import { alignment } from "../models/alignment.enum";
import { UnitAmount } from "../models/column";
import { bodiesEnum } from "./models/bodies.enum";
import { columnDefs, subtypeEnum, variantEnum } from "./models/column-definition";
import { columnType } from "./models/column-type.enum";
import { crownType } from "./models/crown-type.enum";
import { headType } from "./models/head-type.enum";
import { middleType } from "./models/middle-type.enum";
import { partTypeEnum } from "./models/part-type.enum";
import { plinthType } from "./models/plinth-type.enum";
import { CorridorColumn, CorridorPart } from "./models/props-config";
import { segmentType } from "./models/segment-type";

export interface Config {
    amount_C_XS_Columns: number;
    amount_C_M_Columns: number;
    amount_C_S_Columns: number;
    amount_C_L_Columns: number;
    amount_C_XL_Columns: number;
    selectedPlinthType: plinthType,
    selectedMiddleType: middleType,
    selectedCrownType: crownType,
    plinthFloors: number,
    middleFloors: number,
    crownFloors: number,
    selectedMoosPlazaType: columnType,
    moosPlazaPosition: bodiesEnum,
    selectedMoosPlazaAlignment: alignment,
    selectedHeadType: headType,
    nrOfStaircaseCores: number,
    nrOfElevatorCores: number,
    customPlinthHeight: number,
    customPlinthWidth: number,
}
export class ColumnService extends ColumnBaseService {

    getTotalAmountOfAllUnits(parts: CorridorPart[]): UnitAmount[] {
        let unitAmounts: UnitAmount[] = [];
        if (parts) {
            unitAmounts = this.computeTotalAmountsForParts(parts);
            const totalUnits = this.totalUnits(unitAmounts);

            unitAmounts.forEach(unitAmount => {
                unitAmount.percentage = unitAmount.amount / totalUnits * 100;
            });
        }
        return unitAmounts;
    }

    totalUnits(units: UnitAmount[]) {
        return units.map(u => u.amount).reduce((p, c) => p + c, 0);
    }

    createPossibleColumnDefinitions(
        c: Config
    ): CorridorPart[] {

        const headLeftPart: CorridorPart = {
            type: partTypeEnum.headLeft,
            columns: [],
        }
        const bodyAPart: CorridorPart = {
            type: partTypeEnum.bodyA,
            columns: [],
        }
        const bodyBPart: CorridorPart = {
            type: partTypeEnum.bodyB,
            columns: [],
        }
        const headRightPart: CorridorPart = {
            type: partTypeEnum.headRight,
            columns: [],
        }

        const ch = c.customPlinthHeight;
        const cw = c.customPlinthWidth;

        // Heads
        if (c.selectedHeadType !== headType.none) {
            headLeftPart.columns.push(this.createHeadColumn(alignment.left, c.selectedHeadType, c.selectedPlinthType, c.selectedMiddleType, c.selectedCrownType, c.plinthFloors, c.middleFloors, c.crownFloors, c.customPlinthHeight, c.customPlinthWidth))
        }

        let baysToFill = 0;
        // extra staircases

        if (c.nrOfStaircaseCores > 0) {
            baysToFill += c.nrOfStaircaseCores;
            if (c.moosPlazaPosition === bodiesEnum.A) {
                for (let i = 0; i < c.nrOfStaircaseCores; i++) {
                    const col = this.createColumnFromDefinition(columnType.O_TL, c.selectedPlinthType, subtypeEnum.single, subtypeEnum.single, c.plinthFloors, c.middleFloors, c.crownFloors, variantEnum.single, ch, cw);
                    bodyAPart.columns.push(col)
                }
            } else if (c.moosPlazaPosition === bodiesEnum.B) {
                for (let i = 0; i < c.nrOfStaircaseCores; i++) {
                    const col = this.createColumnFromDefinition(columnType.O_TL, c.selectedPlinthType, subtypeEnum.single, subtypeEnum.single, c.plinthFloors, c.middleFloors, c.crownFloors, variantEnum.single, ch, cw);
                    bodyBPart.columns.push(col)
                }
            }
        }
        if (c.nrOfElevatorCores > 0) {
            baysToFill += c.nrOfElevatorCores;
            if (c.moosPlazaPosition === bodiesEnum.A) {
                for (let i = 0; i < c.nrOfElevatorCores; i++) {
                    const col = this.createColumnFromDefinition(columnType.O_TL, c.selectedPlinthType, subtypeEnum.single, subtypeEnum.single, c.plinthFloors, c.middleFloors, c.crownFloors, variantEnum.single, ch, cw);
                    bodyAPart.columns.push(col)
                }
            } else if (c.moosPlazaPosition === bodiesEnum.B) {
                for (let i = 0; i < c.nrOfElevatorCores; i++) {
                    const col = this.createColumnFromDefinition(columnType.O_TL, c.selectedPlinthType, subtypeEnum.single, subtypeEnum.single, c.plinthFloors, c.middleFloors, c.crownFloors, variantEnum.single, ch, cw);
                    bodyBPart.columns.push(col)
                }
            }
        }

        // Moos plein
        if (c.selectedMoosPlazaType !== columnType.none) {
            let moospleinColumnVariant = variantEnum.left;
            if (c.moosPlazaPosition === bodiesEnum.A) {
                if (c.selectedMoosPlazaAlignment === alignment.right) {
                    moospleinColumnVariant = variantEnum.right;
                }
            } else if (c.moosPlazaPosition === bodiesEnum.B) {
                if (c.selectedMoosPlazaAlignment === alignment.left) {
                    moospleinColumnVariant = variantEnum.right;
                } else {
                    moospleinColumnVariant = variantEnum.left;
                }
            }

            const col = this.createColumnFromDefinition(c.selectedMoosPlazaType as any, c.selectedPlinthType, c.selectedMiddleType, c.selectedCrownType, c.plinthFloors, c.middleFloors, c.crownFloors, moospleinColumnVariant, ch, cw);
            baysToFill += col.bays;
            this.addColumnToRightBody(c.moosPlazaPosition, bodyAPart, bodyBPart, col);

            this.fillReplacements(c.moosPlazaPosition, baysToFill, bodyAPart, bodyBPart, c);
        } else {
            this.fillReplacements(c.moosPlazaPosition, baysToFill, bodyAPart, bodyBPart, c);
        }
        // Body columns
        if (c.amount_C_XS_Columns > 0) {
            for (let i = 0; i < c.amount_C_XS_Columns; i++) {
                const cxsCol = this.createColumnFromDefinition(columnType.C_XS, c.selectedPlinthType, c.selectedMiddleType, c.selectedCrownType, c.plinthFloors, c.middleFloors, c.crownFloors, variantEnum.single, ch, cw);
                bodyAPart.columns.push(cxsCol);
                bodyBPart.columns.push({ ...cxsCol, id: Math.random().toString() });
            }
        }
        if (c.amount_C_M_Columns > 0) {
            for (let i = 0; i < c.amount_C_M_Columns; i++) {
                const cmCol = this.createColumnFromDefinition(columnType.C_M, c.selectedPlinthType, c.selectedMiddleType, c.selectedCrownType, c.plinthFloors, c.middleFloors, c.crownFloors, variantEnum.single, ch, cw);
                bodyAPart.columns.push(cmCol);
                bodyBPart.columns.push({ ...cmCol, id: Math.random().toString() });
            }
        }
        if (c.amount_C_S_Columns > 0) {
            for (let i = 0; i < c.amount_C_S_Columns; i++) {
                const csCol = this.createColumnFromDefinition(columnType.C_S1S2, c.selectedPlinthType, c.selectedMiddleType, c.selectedCrownType, c.plinthFloors, c.middleFloors, c.crownFloors, variantEnum.single, ch, cw);
                bodyAPart.columns.push(csCol);
                bodyBPart.columns.push({ ...csCol, id: Math.random().toString() });
            }
        }
        if (c.amount_C_L_Columns > 0) {
            for (let i = 0; i < c.amount_C_L_Columns; i++) {
                const clCol = this.createColumnFromDefinition(columnType.C_L, c.selectedPlinthType, c.selectedMiddleType, c.selectedCrownType, c.plinthFloors, c.middleFloors, c.crownFloors, variantEnum.single, ch, cw);
                bodyAPart.columns.push(clCol);
                bodyBPart.columns.push({ ...clCol, id: Math.random().toString() });
            }
        }
        if (c.amount_C_XL_Columns > 0) {
            for (let i = 0; i < c.amount_C_XL_Columns; i++) {
                const cxlCol = this.createColumnFromDefinition(columnType.C_XL, c.selectedPlinthType, c.selectedMiddleType, c.selectedCrownType, c.plinthFloors, c.middleFloors, c.crownFloors, variantEnum.single, ch, cw);
                bodyAPart.columns.push(cxlCol);
                bodyBPart.columns.push({ ...cxlCol, id: Math.random().toString() });
            }
        }

        // Last column is right head type if it exists
        if (c.selectedHeadType !== headType.none) {
            headRightPart.columns.push(this.createHeadColumn(alignment.right, c.selectedHeadType, c.selectedPlinthType, c.selectedMiddleType, c.selectedCrownType, c.plinthFloors, c.middleFloors, c.crownFloors, c.customPlinthHeight, c.customPlinthWidth))
        }

        return [headLeftPart, bodyAPart, bodyBPart, headRightPart];
    }

    // This method is super manual, but solves the problem for now, it will need to become more intelligent
    private fillReplacements(
        moosPlazaPosition: bodiesEnum,
        baysToFill: number,
        bodyAPart: CorridorPart,
        bodyBPart: CorridorPart,
        c: Config,
    ) {
        const ch = c.customPlinthHeight;
        const cw = c.customPlinthWidth;

        let remainderA = [];
        let remainderB = [];

        // options for knapsack to choose from. This is reasonably long array of items with repeating names to make knapsack solution usable in our case
        var items2 = [
            { w: 1, b: 1, name: "cxs" },
            { w: 2, b: 2, name: "cm" },
            { w: 3, b: 3, name: "cs" },
            { w: 3, b: 3, name: "cl" },
            { w: 3, b: 3, name: "cxl" },
            { w: 1, b: 1, name: "cxs" },
            { w: 2, b: 2, name: "cm" },
            { w: 3, b: 3, name: "cs" },
            { w: 3, b: 3, name: "cl" },
            { w: 3, b: 3, name: "cxl" },
            { w: 1, b: 1, name: "cxs" },
            { w: 2, b: 2, name: "cm" },
            { w: 3, b: 3, name: "cs" },
            { w: 3, b: 3, name: "cl" },
            { w: 3, b: 3, name: "cxl" },
            { w: 1, b: 1, name: "cxs" },
            { w: 2, b: 2, name: "cm" },
            { w: 3, b: 3, name: "cs" },
            { w: 3, b: 3, name: "cl" },
            { w: 3, b: 3, name: "cxl" },
            { w: 1, b: 1, name: "cxs" },
            { w: 2, b: 2, name: "cm" },
            { w: 3, b: 3, name: "cs" },
            { w: 3, b: 3, name: "cl" },
            { w: 3, b: 3, name: "cxl" },
            { w: 1, b: 1, name: "cxs" },
            { w: 2, b: 2, name: "cm" },
            { w: 3, b: 3, name: "cs" },
            { w: 3, b: 3, name: "cl" },
            { w: 3, b: 3, name: "cxl" },
            { w: 1, b: 1, name: "cxs" },
            { w: 2, b: 2, name: "cm" },
            { w: 3, b: 3, name: "cs" },
            { w: 3, b: 3, name: "cl" },
            { w: 3, b: 3, name: "cxl" },
        ];

        const res = ServiceManager.utilityService.knapsack(items2, baysToFill);

        res.solution.forEach(obj => {
            if (obj.name === 'cxs') {
                const cmColA = this.createColumnFromDefinition(columnType.C_XS, c.selectedPlinthType, c.selectedMiddleType, c.selectedCrownType, c.plinthFloors, c.middleFloors, c.crownFloors, variantEnum.single, ch, cw);
                const cmColB = this.createColumnFromDefinition(columnType.C_XS, c.selectedPlinthType, c.selectedMiddleType, c.selectedCrownType, c.plinthFloors, c.middleFloors, c.crownFloors, variantEnum.single, ch, cw);
                remainderA.push(cmColA);
                remainderB.push(cmColB);
            } else if (obj.name === 'cm') {
                const cmColA = this.createColumnFromDefinition(columnType.C_M, c.selectedPlinthType, c.selectedMiddleType, c.selectedCrownType, c.plinthFloors, c.middleFloors, c.crownFloors, variantEnum.single, ch, cw);
                const cmColB = this.createColumnFromDefinition(columnType.C_M, c.selectedPlinthType, c.selectedMiddleType, c.selectedCrownType, c.plinthFloors, c.middleFloors, c.crownFloors, variantEnum.single, ch, cw);
                remainderA.push(cmColA);
                remainderB.push(cmColB);
            } else if (obj.name === 'cs') {
                const csColA = this.createColumnFromDefinition(columnType.C_S1S2, c.selectedPlinthType, c.selectedMiddleType, c.selectedCrownType, c.plinthFloors, c.middleFloors, c.crownFloors, variantEnum.single, ch, cw);
                const csColB = this.createColumnFromDefinition(columnType.C_S1S2, c.selectedPlinthType, c.selectedMiddleType, c.selectedCrownType, c.plinthFloors, c.middleFloors, c.crownFloors, variantEnum.single, ch, cw);
                remainderA.push(csColA);
                remainderB.push(csColB);
            } else if (obj.name === 'cl') {
                const clColA = this.createColumnFromDefinition(columnType.C_L, c.selectedPlinthType, c.selectedMiddleType, c.selectedCrownType, c.plinthFloors, c.middleFloors, c.crownFloors, variantEnum.single, ch, cw);
                const clColB = this.createColumnFromDefinition(columnType.C_L, c.selectedPlinthType, c.selectedMiddleType, c.selectedCrownType, c.plinthFloors, c.middleFloors, c.crownFloors, variantEnum.single, ch, cw);
                remainderA.push(clColA);
                remainderB.push(clColB);
            } else if (obj.name === 'cxl') {
                const clColA = this.createColumnFromDefinition(columnType.C_XL, c.selectedPlinthType, c.selectedMiddleType, c.selectedCrownType, c.plinthFloors, c.middleFloors, c.crownFloors, variantEnum.single, ch, cw);
                const clColB = this.createColumnFromDefinition(columnType.C_XL, c.selectedPlinthType, c.selectedMiddleType, c.selectedCrownType, c.plinthFloors, c.middleFloors, c.crownFloors, variantEnum.single, ch, cw);
                remainderA.push(clColA);
                remainderB.push(clColB);
            }
        });

        if (moosPlazaPosition === bodiesEnum.A) {
            bodyBPart.columns.push(...remainderA);
        } else {
            bodyAPart.columns.push(...remainderB);
        }
    }

    private addColumnToRightBody(moosPlazaPosition: bodiesEnum, bodyAPart: CorridorPart, bodyBPart: CorridorPart, col: CorridorColumn) {
        if (moosPlazaPosition === bodiesEnum.A) {
            bodyAPart.columns.push(col);
        } else {
            bodyBPart.columns.push(col);
        }
    }

    createHeadColumn(
        headAlignment: alignment,
        selectedHeadType: headType,
        selectedPlinthType: plinthType,
        selectedMiddleType: middleType,
        selectedCrownType: crownType,
        plinthFloors: number,
        middleFloors: number,
        crownFloors: number,
        customPlinthHeight: number,
        customPlinthWidth: number
    ) {
        let result: CorridorColumn;
        let headVariant = variantEnum.left;
        if (headAlignment === alignment.right) {
            headVariant = variantEnum.right;
        }
        const ch = customPlinthHeight;
        const cw = customPlinthWidth;

        result = this.createColumnFromDefinition(selectedHeadType, selectedPlinthType, selectedMiddleType, selectedCrownType, plinthFloors, middleFloors, crownFloors, headVariant, ch, cw)

        return result;
    }

    createColumnFromDefinition(type: columnType | headType, plinth: plinthType | subtypeEnum, middle: middleType | subtypeEnum, crown: crownType | subtypeEnum, plinthFloors: number, middleFloors: number, crownFloors: number, variant: variantEnum, customPlinthHeight: number, customPlinthWidth: number): CorridorColumn {
        const columnDefinition = columnDefs.find(s => s.type === type);
     
        const bays = columnDefinition.bays;

        let plinthSections: sectionType[] = [sectionType.none];

        let amountOfUnits = [];
        if (plinth !== plinthType.Custom) {
            if ([columnType.O_TL].includes(type as columnType)) {
                plinth = subtypeEnum.single;
            }
            const plinthDef = columnDefinition.segmentDefinitions.find(s => s.type === segmentType.plinth && s.subtype === plinth);
            const plinthFloorDef = plinthDef.floorVariants.find(s => s.floors === plinthFloors);
            const plinthSideDef = plinthFloorDef.variants.find(f => f.variant === variant);

            plinthSections = plinthSideDef.sections;
        }

        let middleSections: sectionType[];

        if ([columnType.O_TL].includes(type as columnType)) {
            middle = subtypeEnum.single;
            crown = subtypeEnum.single;
        }

        const middleDef = columnDefinition.segmentDefinitions.find(s => s.type === segmentType.middle && s.subtype === middle);

        const middleFloorDef = middleDef.floorVariants.find(s => s.floors === 1);

        const middleSideDef = middleFloorDef.variants.find(f => f.variant === variant);

        middleSections = middleSideDef.sections;

        let crownSections: sectionType[];

        const crownDef = columnDefinition.segmentDefinitions.find(s => s.type === segmentType.crown && s.subtype === crown);
        const crownFloorDef = crownDef.floorVariants.find(s => s.floors === crownFloors);

        const crownVariantDef = crownFloorDef.variants.find(f => f.variant === variant);
        crownSections = crownVariantDef.sections;

        let result: CorridorColumn = {
            id: Math.random().toString(),
            type,
            bays,
            segments: [
                {
                    type: segmentType.plinth,
                    sectionTypes: plinthSections,
                    subtype: plinth as subtypeEnum,
                    custom: plinth === plinthType.Custom,
                    floorsInSegment: plinthFloors,
                    customSegmentHeight: (plinth === plinthType.Custom ? customPlinthHeight : 0),
                    customSegmentWidth: (plinth === plinthType.Custom ? customPlinthWidth : 0)
                },
                {
                    type: segmentType.middle,
                    subtype: middle as subtypeEnum,
                    sectionTypes: middleSections,
                    floorsInSegment: middleFloors
                },
                {
                    type: segmentType.crown,
                    subtype: crown as subtypeEnum,
                    sectionTypes: crownSections,
                    floorsInSegment: crownFloors
                }
            ],
            amountOfUnits,
        }

        return result;
    }

}